import React, { FC, Fragment, useState, useEffect } from 'react';
import { Alert, Box, FormControlLabel, Switch, Tab, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { LabClient } from 'models/client-directory';
import ClientContactDetails from './client-contact-details/client-contact-details.component';
import InternalInformation from './internal-information/internal-information.component';
import Settings from './settings/settings.component';
import { toggleCustomerEnabledTemplate } from 'apollo/templates/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

type ClientDetailsProps = {
    labClient: LabClient;
    onClientDetailsSaved?: () => void;
};

const ClientDetails: FC<ClientDetailsProps> = ({ labClient, onClientDetailsSaved }: ClientDetailsProps) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { showBoundary } = useDenthubErrorBoundary();
    const [tabState, setTabState] = useState('#contactDetails');
    const [customerEnabled, setCustomerEnabled] = useState<boolean>(false);
    const [disableConfirmDialogOpen, setDisableConfirmDialogOpen] = useState(false);
    const match = useMatch('/client-directory/clients/:id/:tab');

    const [toggleCustomerStatus] = useMutation(toggleCustomerEnabledTemplate, {
        onCompleted: () => {
            setDisableConfirmDialogOpen(false);
            if (onClientDetailsSaved) {
                onClientDetailsSaved();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setTabState(location.hash);
    }, [location]);

    useEffect(() => {
        if (labClient?.isActive) {
            setCustomerEnabled(labClient.isActive);
        }
    }, [labClient]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`${match?.pathnameBase}${newTabState}`);
            setTabState(newTabState);
        }
    };

    const toggleCustomerEnabled = (isChecked: boolean) => {
        setCustomerEnabled(isChecked);
        if (!isChecked) {
            // Show confirmation before disabling the customer
            setDisableConfirmDialogOpen(true);
            return;
        }

        // If enabling, call mutation directly
        toggleCustomerStatus({
            variables: {
                customerId: labClient.customerId,
                isEnabled: true,
            },
        });
    };

    const handleConfirmDisable = () => {
        toggleCustomerStatus({
            variables: {
                customerId: labClient.customerId,
                isEnabled: false,
            },
        });
    };

    const handleClose = () => {
        setDisableConfirmDialogOpen(false);
        setCustomerEnabled(true);
    };

    return (
        <Fragment>
            <Typography mb={1} variant="h2">
                {translate('client-directory:CLIENT_DETAILS')}
            </Typography>
            <TabContext value={tabState}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label={translate('client-directory:CLIENT_CONTACT_DETAILS')} value="#contactDetails" />
                        <Tab label={translate('client-directory:INTERNAL_INFORMATION')} value="#internalInformation" />
                        <Tab label={translate('common:SETTINGS')} value="#settings" />
                    </TabList>
                    {!labClient.customer.parentClinicId && (
                        <Box display="flex">
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="secondary"
                                        checked={customerEnabled}
                                        onChange={(_, value) => toggleCustomerEnabled(value)}
                                    />
                                }
                                label={
                                    customerEnabled
                                        ? translate('client-directory:CUSTOMER_ENABLED')
                                        : translate('client-directory:CUSTOMER_DISABLED')
                                }
                            />
                        </Box>
                    )}
                </Box>
                <Box sx={{ maxWidth: 900 }}>
                    <TabPanel sx={{ padding: 0 }} value="#contactDetails">
                        <ClientContactDetails labClient={labClient} onContactSaved={onClientDetailsSaved} />
                    </TabPanel>
                    <TabPanel sx={{ padding: 0 }} value="#internalInformation">
                        <InternalInformation labClient={labClient} onAccountManagerSaved={onClientDetailsSaved} />
                    </TabPanel>
                    <TabPanel sx={{ padding: 0 }} value="#settings">
                        <Settings labClient={labClient} onSettingsSaved={onClientDetailsSaved} />
                    </TabPanel>
                </Box>
            </TabContext>

            {disableConfirmDialogOpen && (
                <ConfirmDialog
                    open={disableConfirmDialogOpen}
                    handleClose={handleClose}
                    dialogTitle={translate('common:ARE_YOU_SURE')}
                    dialogContent={
                        <Alert severity="warning" sx={{ marginBottom: 2 }}>
                            {translate('client-directory:CONFIRM_CUSTOMER_DISABLE')}
                        </Alert>
                    }
                    handleConfirm={handleConfirmDisable}
                />
            )}
        </Fragment>
    );
};

export default ClientDetails;
